import { Building2, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { SkeletonLines } from '@/components/ui/Skeleton';
import {
  INSTITUTION_TYPE_LABEL,
  SCHOOL_STATUS_LABEL,
  SCHOOL_STATUS_TONE,
} from '@/constants/institute';
import { useApiQuery } from '@/hooks/useApiQuery';
import { formatCurrency, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { CatalogueFeature } from '@/types/platformFeature';
import { featuresApi } from '../api/features.api';

export interface FeatureBuyersPanelProps {
  /** The feature whose customers to list, or null before one is chosen. */
  feature: CatalogueFeature | null;
  className?: string;
}

/**
 * Which schools and colleges have one feature, and where it stands for each.
 *
 * "Has it" is what the tracker records: a live row means the customer asked for
 * it. Active says whether they can use it, the same switch the tracker shows.
 * Each name links to that customer's tracker, which is where it is changed.
 */
export function FeatureBuyersPanel({ feature, className }: FeatureBuyersPanelProps) {
  const { data, isLoading, error, refetch } = useApiQuery(
    () => (feature ? featuresApi.institutesWith(feature.uuid) : Promise.resolve(null)),
    // The count changes when a feature is added to or removed from a tracker,
    // so it is part of the key: a stale list under a fresh count would disagree.
    [feature?.uuid, feature?.instituteCount],
  );

  if (!feature) {
    return (
      <section className={cn('card p-5', className)}>
        <EmptyState
          icon={<Building2 className="h-6 w-6" />}
          title="Choose a feature"
          description="Pick a feature to see which schools and colleges have it."
        />
      </section>
    );
  }

  const institutes = data ?? [];

  return (
    <section className={cn('card flex flex-col gap-4 p-5', className)} aria-labelledby="buyers-heading">
      <header className="flex flex-col gap-1 border-b border-line pb-3">
        <span className="lbl">Requested by</span>
        <h2 id="buyers-heading" className="text-lg text-ink">
          {feature.name}
        </h2>
        <p className="text-sm text-ink-3">
          {feature.price === null ? 'Part of the base licence' : formatCurrency(feature.price)} ·{' '}
          {feature.instituteCount} institute{feature.instituteCount === 1 ? '' : 's'} · active at{' '}
          {feature.enabledInstituteCount}
        </p>
      </header>

      {isLoading && <SkeletonLines count={5} />}

      {!isLoading && error && (
        <QueryError title="Could not load who has this feature" error={error} onRetry={refetch} />
      )}

      {!isLoading && !error && institutes.length === 0 && (
        <EmptyState
          title="Nobody has this yet"
          description="Add it to an institute from the Feature tracker."
          action={
            <Link to="/platform/features" className="text-sm font-[560] text-accent hover:text-ink">
              Open the Feature tracker
            </Link>
          }
        />
      )}

      {!isLoading && !error && institutes.length > 0 && (
        <ul className="flex flex-col divide-y divide-line">
          {institutes.map((institute) => (
            <li key={institute.uuid} className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0">
              <div className="flex items-start justify-between gap-2">
                <Link
                  to={`/platform/features?institute=${institute.uuid}`}
                  className="group flex min-w-0 items-center gap-1 rounded-field focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
                >
                  <span className="truncate text-sm font-[560] text-ink group-hover:text-accent">
                    {institute.displayName}
                  </span>
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden />
                </Link>
                <Badge tone={SCHOOL_STATUS_TONE[institute.status]} withDot className="shrink-0">
                  {SCHOOL_STATUS_LABEL[institute.status]}
                </Badge>
              </div>

              <p className="truncate text-xs text-ink-3">
                <span className="font-mono uppercase">{institute.code}</span> ·{' '}
                {INSTITUTION_TYPE_LABEL[institute.institutionType]} · {institute.city},{' '}
                {institute.state}
              </p>

              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <span
                  className={cn('pill', institute.isEnabled ? 'bg-accent-soft text-accent' : 'bg-surface-2 text-ink-3')}
                >
                  {institute.isEnabled ? 'Active' : 'Not active'}
                </span>
                <span className="text-ink-3">since {formatDate(institute.since)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
